import React from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { TAG_SHAPES, TagPreview } from "../../../lib/tagShapes";
import { TextInput, Toggle } from "../shared";

export const ShapesPanel = ({ shapes, setShapes, inventory, newShape, setNewShape, onPatch, onMove, onAdd, onDelete }) => (
  <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6 mb-8" data-testid="shapes-panel">
    <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-5">TAG SHAPES</h2>
    <div className="space-y-3">
      {shapes.map((s, i) => (
        <div key={s.id} className="bg-cream rounded-2xl p-3" data-testid={`shape-row-${s.key}`}>
          <div className="flex items-center gap-3">
            <div className="flex flex-col">
              <button onClick={() => onMove(i, -1)} disabled={i === 0} className="text-plum-deep/40 hover:text-magenta disabled:opacity-20" data-testid={`shape-up-${s.key}`} aria-label="Move up"><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => onMove(i, 1)} disabled={i === shapes.length - 1} className="text-plum-deep/40 hover:text-magenta disabled:opacity-20" data-testid={`shape-down-${s.key}`} aria-label="Move down"><ChevronDown className="w-4 h-4" /></button>
            </div>
            <TagPreview shapeKey={s.key} hex={s.active ? "#EC2675" : "#E1CFF2"} className="w-9 h-12 shrink-0" />
            <TextInput value={s.name} onChange={(e) => setShapes((l) => l.map((x) => (x.id === s.id ? { ...x, name: e.target.value } : x)))} onBlur={(e) => onPatch(s.id, { name: e.target.value })} className="flex-1" />
            <Toggle checked={s.active} onChange={() => onPatch(s.id, { active: !s.active })} testid={`shape-toggle-${s.key}`} />
            <button onClick={() => onDelete(s.id)} className="text-plum-deep/40 hover:text-red-600" data-testid={`shape-delete-${s.key}`} aria-label="Delete shape">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-2 flex items-center gap-4 flex-wrap pl-9">
            <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-plum-deep/50 font-semibold">
              PRICE $
              <TextInput
                type="number" step="0.5" value={s.price ?? ""}
                onChange={(e) => setShapes((l) => l.map((x) => (x.id === s.id ? { ...x, price: e.target.value } : x)))}
                onBlur={(e) => onPatch(s.id, { price: Number(e.target.value) || 0 })}
                data-testid={`shape-price-${s.key}`} className="w-20 !px-3"
              />
            </label>
            <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-plum-deep/50 font-semibold">
              MADE IN
              <TextInput
                type="number" value={s.production_days ?? ""}
                onChange={(e) => setShapes((l) => l.map((x) => (x.id === s.id ? { ...x, production_days: e.target.value } : x)))}
                onBlur={(e) => onPatch(s.id, { production_days: Number(e.target.value) || 0 })}
                data-testid={`shape-days-${s.key}`} className="w-16 !px-3"
              />
              DAYS
            </label>
            <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-plum-deep/50 font-semibold">
              STOCK LINK
              <select
                value={s.inventory_item_id || ""}
                onChange={(e) => onPatch(s.id, { inventory_item_id: e.target.value })}
                data-testid={`shape-inventory-${s.key}`}
                className="rounded-full px-3 py-1.5 bg-warmwhite border border-plum-deep/15 text-xs focus:outline-none max-w-[180px]"
              >
                <option value="">— NONE —</option>
                {inventory.map((it) => <option key={it.id} value={it.id}>{it.name} ({it.stock})</option>)}
              </select>
            </label>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-5 flex items-center gap-3 flex-wrap">
      <select value={newShape.key} onChange={(e) => setNewShape({ ...newShape, key: e.target.value })} data-testid="new-shape-key" className="rounded-full px-4 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none">
        {Object.entries(TAG_SHAPES).map(([key, s]) => <option key={key} value={key}>{s.label}</option>)}
      </select>
      <TextInput placeholder="Display name" value={newShape.name} onChange={(e) => setNewShape({ ...newShape, name: e.target.value })} data-testid="new-shape-name" className="flex-1 min-w-[140px]" />
      <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-4 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta" data-testid="add-shape-btn">
        <Plus className="w-3.5 h-3.5" /> ADD
      </button>
    </div>
  </div>
);

export const ColoursPanel = ({ colours, setColours, newColour, setNewColour, onPatch, onAdd, onDelete }) => (
  <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6" data-testid="colours-panel">
    <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-5">TAG COLOURS</h2>
    <div className="space-y-3">
      {colours.map((c) => (
        <div key={c.id} className="flex items-center gap-3 bg-cream rounded-2xl p-3" data-testid={`colour-row-${c.id}`}>
          <input type="color" value={c.hex} onChange={(e) => onPatch(c.id, { hex: e.target.value })} className="w-10 h-10 rounded-full border-0 bg-transparent cursor-pointer shrink-0" data-testid={`colour-picker-${c.id}`} />
          <TextInput value={c.name} onChange={(e) => setColours((l) => l.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)))} onBlur={(e) => onPatch(c.id, { name: e.target.value })} className="flex-1" />
          <Toggle checked={c.active} onChange={() => onPatch(c.id, { active: !c.active })} testid={`colour-toggle-${c.id}`} />
          <button onClick={() => onDelete(c.id)} className="text-plum-deep/40 hover:text-red-600" data-testid={`colour-delete-${c.id}`} aria-label="Delete colour">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
    <div className="mt-5 flex items-center gap-3 flex-wrap">
      <input type="color" value={newColour.hex} onChange={(e) => setNewColour({ ...newColour, hex: e.target.value })} className="w-10 h-10 rounded-full border-0 bg-transparent cursor-pointer" data-testid="new-colour-hex" />
      <TextInput placeholder="Colour name" value={newColour.name} onChange={(e) => setNewColour({ ...newColour, name: e.target.value })} data-testid="new-colour-name" className="flex-1 min-w-[140px]" />
      <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-full bg-plum text-cream px-4 py-2.5 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta" data-testid="add-colour-btn">
        <Plus className="w-3.5 h-3.5" /> ADD
      </button>
    </div>
  </div>
);
